import { useLayoutEffect } from 'react';

/**
 * Global reference counting for body scroll lock
 * Ensures multiple overlays (modals, drawers) can lock scroll simultaneously
 * without conflicts or premature unlocking
 */
let lockCount = 0;
let originalStyles: {
  overflow: string;
  paddingRight: string;
} | null = null;

/**
 * useBodyScrollLock - World-class scroll lock with reference counting
 *
 * Based on industry-standard pattern used by Radix UI, React Aria, etc.
 *
 * Features:
 * - Reference counting: Multiple components can lock simultaneously
 * - Only unlocks when LAST lock releases
 * - Prevents content shift with scrollbar compensation
 * - Safe cleanup regardless of unmount order
 *
 * Usage:
 * ```
 * useBodyScrollLock(isModalOpen);
 * ```
 */
export function useBodyScrollLock(isLocked: boolean): void {
  useLayoutEffect(() => {
    if (!isLocked) {
      return;
    }

    // Increment lock count
    lockCount++;

    // First lock - save original styles and apply lock
    if (lockCount === 1) {
      originalStyles = {
        overflow: document.body.style.overflow,
        paddingRight: document.body.style.paddingRight,
      };

      // Lock scroll
      document.body.style.overflow = 'hidden';

      // Prevent content shift by adding padding equal to scrollbar width
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    }

    // Cleanup function
    return () => {
      lockCount--;

      // Last lock released - restore original styles
      if (lockCount === 0 && originalStyles) {
        document.body.style.overflow = originalStyles.overflow;
        document.body.style.paddingRight = originalStyles.paddingRight;
        originalStyles = null;
      }
    };
  }, [isLocked]);
}